import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Alert, Spinner, Form, Button, Modal } from 'react-bootstrap';
import {
  CheckCircle, XCircle, CreditCard, Search, LayoutGrid, List as ListIcon,
  Calendar, User, FileText, AlertTriangle, Building2,
    ClipboardCheck
} from 'lucide-react';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import { format } from 'date-fns';
import PageHeader from '../../../layouts/PageHeader';
import { ListToolbar, FilterChips } from '../../Common/ListToolbar';
import EmptyState from '../../Common/EmptyState';
import { useListControls, useViewMode } from '../../../../hooks/useListControls';
import * as api from '../../../../services/api/expensesApi';
import * as projectsApi from '../../../../services/api/projectsApi';
import { useCapabilities } from '../../../../context/CapabilityContext';
import ExpenseDetailModal from './ExpenseDetailModal';
import BulkSettleModal from './BulkSettleModal';

/**
 * ExpenseApprovalQueuePage — the admin's full view of every expense in the
 * company. Renamed in the sidebar to "Hantera utlägg"; the approval inbox
 * role moved to the new ToDo page. This page is now for browsing,
 * filtering, bulk actions, and history.
 *
 * Two view modes (persisted in localStorage):
 *   - kanban: all 5 lanes visible side-by-side, good for a "state of the
 *     world" glance
 *   - list:   single filterable list, good for searching/bulk actions
 */

// Status taxonomy — shared by kanban lanes + list pills.
const STATUSES = [
  { id: 'submitted', label: 'Väntar godkännande', badgeClass: 'exp-badge-warn'  },
  { id: 'approved',  label: 'Godkända',           badgeClass: 'exp-badge-ok'    },
  { id: 'booked',    label: 'Bokförda',           badgeClass: 'exp-badge-info'  },
  { id: 'settled',   label: 'Betalda',            badgeClass: 'exp-badge-done'  },
  { id: 'rejected',  label: 'Avvisade',           badgeClass: 'exp-badge-bad'   },
];

// Phase 2.1/2.2/2.3 — Scope tabs.
// §4.2 — expenses have NO Mitt team scope (no manager-as-approver
// delegation). Only Mina + Alla. Sidebar already gates this whole page on
// `hasScope('expense', 'approve', 'all')` (admin tier), so a regular employee
// only ever lands here via direct URL — Phase 2.5's CapabilityRoute mount
// is the bookmark guard.
const SCOPE_TABS = [
  { id: 'mine', label: 'Mina',  icon: User,      scope: 'mine' },
  { id: 'all',  label: 'Alla',  icon: Building2, scope: 'all'  },
];

const fmtAmount = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const statusLabel = (id) => STATUSES.find((s) => s.id === id)?.label || id;
const statusBadge = (id) => STATUSES.find((s) => s.id === id)?.badgeClass || 'exp-badge-muted';

const ExpenseApprovalQueuePage = () => {
  const { companyId } = useParams();
  const location = useLocation();
  const { capabilities, hasScope, hasLoaded: capsLoaded } = useCapabilities();

  // The status filter keeps the `?status=` key rather than the default
  // `?filter=`: ExpenseTodoContributor on the backend builds deep links with
  // it, so renaming would silently break every "Utlägg att granska" tile.
  const {
    search,
    setSearch,
    debouncedSearch,
    filter: statusFilter,
    setFilter: setStatusFilter,
    isFiltered,
  } = useListControls({ defaultFilter: 'all', defaultSort: null, filterKey: 'status' });
  const [viewMode, changeView] = useViewMode('expAdmin', 'kanban', ['kanban', 'list']);
  // activeTab resolves once /me/capabilities loads — see effect below.
  // Mina visible iff caller has an employeeId mapping; Alla iff admin tier.
  const [activeTab, setActiveTab] = useState(null);
  // Per-lane recency filter for terminal lanes in the kanban — keeps the
  // approval queue tidy without losing access to history. Same defaults
  // and dropdown options as the time-reporting and own-expenses kanbans.
  const [approvedDays, setApprovedDays] = useState(30);
  const [bookedDays, setBookedDays] = useState(30);
  const [settledDays, setSettledDays] = useState(30);
  const [rejectedDays, setRejectedDays] = useState(30);
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [detailExpense, setDetailExpense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [bulkSettleOpen, setBulkSettleOpen] = useState(false);

  // Tab visibility derived from server-issued capabilities. NOT computed
  // from role names — the server is the source of truth.
  const tabVisibility = useMemo(() => ({
    mine: Boolean(capabilities.employeeId),
    all:  hasScope('expense', 'view', 'all'),
  }), [capabilities, hasScope]);

  // Default to the broadest visible tab. An admin-with-mapping (PA2 solo
  // founder) lands on Alla; an admin-without-mapping (PA1+ external
  // bookkeeper) also lands on Alla; a hypothetical user who somehow reached
  // this page with only mine-scope (Phase 2.5 should block them) lands on
  // Mina with empty Alla hidden.
  useEffect(() => {
    if (!capsLoaded || activeTab !== null) return;
    if (tabVisibility.all) setActiveTab('all');
    else if (tabVisibility.mine) setActiveTab('mine');
  }, [capsLoaded, tabVisibility, activeTab]);

  // Fetch everything once per tab — client-side filter keeps view switching
  // instant. The active tab's scope is the server-side gatekeeper.
  const load = useCallback(async () => {
    if (!activeTab) return; // wait for caps + tab resolution
    setLoading(true); setError(null); setSelected({});
    try {
      const tabScope = SCOPE_TABS.find(t => t.id === activeTab)?.scope || 'mine';
      // ?scope=all aliases to /admin/all on the backend (Phase 1.2.4); we
      // call the unified /expenses?scope= endpoint so Mina + Alla travel
      // identical wires and the §4.2 split lives in one place (the server).
      const [expRes, projRes] = await Promise.all([
        api.getExpenses(companyId, { scope: tabScope }),
        projectsApi.getMyProjects(companyId).catch(() => ({ data: { projects: [] } })),
      ]);
      setExpenses(expRes.data?.expenses || []);
      setProjects(projRes.data?.projects || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda utlägg.');
    } finally {
      setLoading(false);
    }
  }, [companyId, activeTab]);

  useEffect(() => { load(); }, [load]);

  // Apply search to everything — the kanban lanes also filter on it.
  const searchFiltered = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    if (!q) return expenses;
    return expenses.filter((ex) =>
      `${ex.employee_firstname || ''} ${ex.employee_lastname || ''}`.toLowerCase().includes(q)
      || (ex.category || '').toLowerCase().includes(q)
      || (ex.description || '').toLowerCase().includes(q));
  }, [expenses, debouncedSearch]);

  // List view: additionally filter by the selected status pill (or skip
  // the status filter entirely when 'all').
  const listFiltered = useMemo(
    () => statusFilter === 'all'
      ? [...searchFiltered].sort((a, b) => String(b.expense_date).localeCompare(String(a.expense_date)))
      : searchFiltered.filter((ex) => ex.status === statusFilter),
    [searchFiltered, statusFilter],
  );

  // Kanban: one array per status, only filled with searchFiltered items.
  // Terminal lanes (approved/booked/settled/rejected) get a recency filter
  // applied; submitted is the active queue and stays unfiltered.
  const { grouped, laneTotals } = useMemo(() => {
    const g = {};
    for (const s of STATUSES) g[s.id] = [];
    for (const ex of searchFiltered) {
      if (g[ex.status]) g[ex.status].push(ex);
    }
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => String(b.expense_date).localeCompare(String(a.expense_date)));
    }
    const totals = {};
    for (const s of STATUSES) totals[s.id] = g[s.id].length;

    const cutoffFromDays = (days) => {
      const c = new Date();
      c.setDate(c.getDate() - days);
      return c;
    };
    const recencyFilter = (items, days, dateOf) => {
      if (!days || days <= 0) return items;
      const cutoff = cutoffFromDays(days);
      return items.filter(e => {
        const d = dateOf(e);
        return d && new Date(d) >= cutoff;
      });
    };
    g.approved = recencyFilter(g.approved, approvedDays, e => e.approved_at || e.updated_at);
    g.booked   = recencyFilter(g.booked,   bookedDays,   e => e.booked_at   || e.updated_at);
    g.settled  = recencyFilter(g.settled,  settledDays,  e => e.settled_at  || e.updated_at);
    g.rejected = recencyFilter(g.rejected, rejectedDays, e => e.updated_at);

    return { grouped: g, laneTotals: totals };
  }, [searchFiltered, approvedDays, bookedDays, settledDays, rejectedDays]);

  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  // Phase 2.4 F4 — "select all" must skip the caller's own rows so a solo
  // founder on the *Alla* tab doesn't accidentally enroll their own expense
  // into a bulk approve. Per-row toggle also disabled in QueueRow below;
  // server still 403s via assertCanApproveExpense as the real gate.
  const ownEmployeeId = capabilities.employeeId;
  const selectableInList = useMemo(
    () => listFiltered.filter((ex) => ex.employee_id !== ownEmployeeId),
    [listFiltered, ownEmployeeId],
  );

  const toggleAll = () => {
    if (selectedIds.length === selectableInList.length) {
      setSelected({});
    } else {
      const s = {};
      selectableInList.forEach((ex) => { s[ex.expense_id] = true; });
      setSelected(s);
    }
  };

  const bulkApprove = async () => {
    if (selectedIds.length === 0) return;
    const confirm = await Swal.fire({
      title: `Godkänn ${selectedIds.length} utlägg?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Godkänn',
      cancelButtonText: 'Avbryt',
    });
    if (!confirm.isConfirmed) return;
    setBusy(true);
    try {
      for (const id of selectedIds) {
        await api.approveExpense(companyId, id);
      }
      toast.success(`${selectedIds.length} utlägg godkända.`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Godkännande misslyckades.');
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    if (!rejectingId || !rejectReason.trim()) return;
    setBusy(true);
    try {
      await api.rejectExpense(companyId, rejectingId, rejectReason.trim());
      toast.success('Utlägg avvisat.');
      setRejectingId(null); setRejectReason('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Avvisning misslyckades.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        icon={ClipboardCheck}
        title="Hantera utlägg"
        subtitle="Utlägg som väntar på din attest."
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* ─── Phase 2.1/2.2/2.3 — Scope tabs (no Mitt team per §4.2) ───
          ABOVE the toolbar, deliberately: the scope decides which QUEUE the
          page shows, while the status chips below the search refine it — and
          both rows have an "Alla" pill, so stacking them together read as one
          confusing filter bar. The scope strip's own margin keeps it clear of
          the search box. */}
      {capsLoaded && (() => {
        const visibleTabs = SCOPE_TABS.filter(t => tabVisibility[t.id]);
        if (visibleTabs.length === 0) {
          return (
            <div className="exp-empty" style={{ marginBottom: 12 }}>
              Du saknar behörighet att se några utlägg här.
            </div>
          );
        }
        // Single visible tab (e.g., admin-without-mapping has only Alla)
        // — render no tab strip; the lone option is implicit.
        if (visibleTabs.length === 1) return null;
        return (
          <div className="exp-pills" role="tablist" aria-label="Omfång">
            {visibleTabs.map(t => {
              const TabIcon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === t.id}
                  className={`exp-pill ${activeTab === t.id ? 'active' : ''}`}
                  onClick={() => {
                    // Reset transient selection when switching scope —
                    // a row selected in *Alla* makes no sense in *Mina*.
                    setActiveTab(t.id);
                    setSelected({});
                  }}
                >
                  <TabIcon size={13} style={{ marginRight: 4 }} />
                  {t.label}
                </button>
              );
            })}
          </div>
        );
      })()}

      <ListToolbar
        className="mb-3"
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Sök anställd, kategori eller beskrivning…',
          ariaLabel: 'Sök bland utlägg',
        }}
        view={{ value: viewMode, onChange: changeView, modes: ['kanban', 'list'] }}
      />

      {/* Status pills — only in list mode (kanban already shows every lane). */}
      {viewMode === 'list' && (
        <FilterChips
          className="mb-3"
          chips={[
            { key: 'all', label: 'Alla', count: searchFiltered.length },
            ...STATUSES.map((st) => ({
              key: st.id,
              label: st.label,
              count: searchFiltered.filter((ex) => ex.status === st.id).length,
            })),
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      )}

      {/* Selection bar */}
      {selectedIds.length > 0 && (() => {
        const selectedExpenses = expenses.filter((e) => selectedIds.includes(e.expense_id));
        const allSubmitted = selectedExpenses.every((e) => e.status === 'submitted');
        const allBooked = selectedExpenses.every((e) => e.status === 'booked' && !e.payroll_id);
        return (
          <div className="exp-selection-bar">
            <span style={{ fontWeight: 600 }}>{selectedIds.length} markerat</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {allSubmitted && (
                <Button variant="success" size="sm" disabled={busy} onClick={bulkApprove}>
                  <CheckCircle size={14} className="me-1" /> Godkänn alla
                </Button>
              )}
              {allBooked && (
                <Button variant="primary" size="sm" disabled={busy} onClick={() => setBulkSettleOpen(true)}>
                  <CreditCard size={14} className="me-1" /> Betala alla
                </Button>
              )}
            </div>
          </div>
        );
      })()}

      {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}

      {/* KANBAN VIEW */}
      {!loading && viewMode === 'kanban' && (
        <div className="exp-kanban">
          {STATUSES.filter((s) => {
            // Hide Avvisade lane when empty — no drag-drop on this page,
            // so we don't need a drop-target reachability escape hatch.
            if (s.id !== 'rejected') return true;
            return grouped.rejected.length > 0;
          }).map((s) => {
            const items = grouped[s.id];
            const isTerminal = s.id === 'approved' || s.id === 'booked' || s.id === 'settled' || s.id === 'rejected';
            const days = s.id === 'approved' ? approvedDays
              : s.id === 'booked' ? bookedDays
              : s.id === 'settled' ? settledDays
              : s.id === 'rejected' ? rejectedDays
              : 0;
            const total = laneTotals[s.id] || 0;
            const showRatio = isTerminal && days > 0 && total > items.length;
            const setDays = (v) => {
              if (s.id === 'approved') setApprovedDays(v);
              else if (s.id === 'booked') setBookedDays(v);
              else if (s.id === 'settled') setSettledDays(v);
              else if (s.id === 'rejected') setRejectedDays(v);
            };
            return (
              <div key={s.id} className="exp-lane">
                <div className="exp-lane-header">
                  <div className="exp-lane-title">
                    {s.label}
                    <span className="exp-lane-count">{showRatio ? `${items.length} av ${total}` : items.length}</span>
                  </div>
                  {isTerminal && (
                    <select
                      className="exp-lane-date-filter"
                      value={days}
                      onChange={(ev) => setDays(Number(ev.target.value))}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <option value={7}>Senaste 7 dagar</option>
                      <option value={30}>Senaste 30 dagar</option>
                      <option value={90}>Senaste 90 dagar</option>
                      <option value={0}>Visa alla</option>
                    </select>
                  )}
                </div>
                <div className="exp-lane-body">
                  {items.length === 0 ? (
                    <div className="exp-lane-empty">Tomt</div>
                  ) : (
                    items.map((ex) => (
                      <QueueCard
                        key={ex.expense_id}
                        expense={ex}
                        onOpen={() => setDetailExpense(ex)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* LIST VIEW */}
      {!loading && viewMode === 'list' && (
        listFiltered.length === 0 ? (
          isFiltered ? (
            <EmptyState variant="no-matches" query={debouncedSearch} />
          ) : (
            <EmptyState
              icon={ClipboardCheck}
              title="Inget att granska"
              description="Utlägg som skickats in för godkännande dyker upp här. Just nu väntar inga på dig."
            />
          )
        ) : (
          <div className="exp-list">
            <div className="exp-list-header">
              <div className="exp-list-col-check">
                <input
                  type="checkbox"
                  checked={selectedIds.length === selectableInList.length && selectableInList.length > 0}
                  onChange={toggleAll}
                  disabled={selectableInList.length === 0}
                  title={selectableInList.length === 0 ? 'Inga utlägg går att markera (du kan inte godkänna ditt eget utlägg).' : undefined}
                />
              </div>
              <div className="exp-list-col-date">Datum</div>
              <div className="exp-list-col-employee">Anställd</div>
              <div className="exp-list-col-desc">Beskrivning</div>
              <div className="exp-list-col-cat">Kategori</div>
              <div className="exp-list-col-amt">Belopp</div>
              <div className="exp-list-col-status">Status</div>
              <div className="exp-list-col-actions" />
            </div>
            {listFiltered.map((ex) => (
              <QueueRow
                key={ex.expense_id}
                expense={ex}
                selected={!!selected[ex.expense_id]}
                onToggle={() => toggle(ex.expense_id)}
                selectable={ex.status === 'submitted' || (ex.status === 'booked' && !ex.payroll_id)}
                onOpen={() => setDetailExpense(ex)}
                onApprove={async () => {
                  try { await api.approveExpense(companyId, ex.expense_id); toast.success('Godkänt.'); await load(); }
                  catch (err) { toast.error(err.response?.data?.message || 'Fel'); }
                }}
                onReject={() => { setRejectingId(ex.expense_id); setRejectReason(''); }}
                isOwn={!!capabilities.employeeId && ex.employee_id === capabilities.employeeId}
              />
            ))}
          </div>
        )
      )}

      {/* Reject modal */}
      <Modal show={!!rejectingId} onHide={() => { setRejectingId(null); setRejectReason(''); }}>
        <Modal.Header closeButton><Modal.Title>Avvisa utlägg</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Label>Ange skäl</Form.Label>
          <Form.Control
            as="textarea" rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Skälet lagras på utlägget så att den anställde kan se det."
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => { setRejectingId(null); setRejectReason(''); }}>Avbryt</Button>
          <Button variant="danger" onClick={submitReject} disabled={busy || !rejectReason.trim()}>
            Avvisa
          </Button>
        </Modal.Footer>
      </Modal>

      <ExpenseDetailModal
        expense={detailExpense}
        companyId={companyId}
        projects={projects}
        adminMode={true}
        onClose={() => setDetailExpense(null)}
        onChanged={async () => { setDetailExpense(null); await load(); }}
      />

      <BulkSettleModal
        companyId={companyId}
        expenses={expenses.filter((e) => selectedIds.includes(e.expense_id))}
        show={bulkSettleOpen}
        onClose={() => setBulkSettleOpen(false)}
        onSettled={async () => { setBulkSettleOpen(false); setSelected({}); await load(); }}
      />

      <style>{expQueueCss}</style>
    </>
  );
};

/* ─── Kanban card ─── */
// Status badge intentionally omitted here — the card's lane already
// conveys the status. Keep it on the list view (where rows mix statuses)
// and on the detail modal.
const QueueCard = ({ expense, onOpen }) => (
  <div className="exp-card" onClick={onOpen} role="button" tabIndex={0}
       onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}>
    <div className="exp-card-top">
      <div className="exp-card-title" title={expense.description}>{expense.description}</div>
    </div>
    <div className="exp-card-meta">
      <span className="exp-card-meta-item"><User size={11} /> {expense.employee_firstname} {expense.employee_lastname || ''}</span>
      <span className="exp-card-meta-item"><Calendar size={11} /> {format(new Date(expense.expense_date), 'd MMM')}</span>
    </div>
    <div className="exp-card-bottom">
      <span className="exp-card-cat">{expense.category}</span>
      <span className="exp-card-amt">{fmtAmount(expense.amount)} <span style={{ fontWeight: 400, fontSize: 10, color: '#64748b' }}>{expense.currency || 'SEK'}</span></span>
    </div>
  </div>
);

/* ─── List row ─── */
// `isOwn` (Phase 2.4) — when the caller's employeeId matches the row's
// employee_id, the row's checkbox + Approve/Reject buttons are disabled
// with a tooltip. Server still rejects via `assertCanApproveExpense`'s
// F4 row-level guard (Phase 1.2.3); this is just the UX layer so a solo
// founder on the *Alla* tab doesn't see "click → 403 toast on own row."
const QueueRow = ({ expense, selected, selectable, onToggle, onOpen, onApprove, onReject, isOwn }) => {
  const ownDisabledTitle = 'Du kan inte godkänna ditt eget utlägg.';
  return (
    <div className="exp-list-row" onClick={onOpen}>
      <div className="exp-list-col-check" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={!selectable || isOwn}
          title={isOwn ? ownDisabledTitle : undefined}
        />
      </div>
      <div className="exp-list-col-date">{expense.expense_date?.slice(0, 10)}</div>
      <div className="exp-list-col-employee">{expense.employee_firstname} {expense.employee_lastname || ''}</div>
      <div className="exp-list-col-desc" title={expense.description}>{expense.description}</div>
      <div className="exp-list-col-cat"><span className="exp-cat-chip">{expense.category}</span></div>
      <div className="exp-list-col-amt">{fmtAmount(expense.amount)} <span style={{ fontWeight: 400, fontSize: 11, color: '#64748b' }}>{expense.currency || 'SEK'}</span></div>
      <div className="exp-list-col-status">
        <span className={`exp-badge ${statusBadge(expense.status)}`}>{statusLabel(expense.status)}</span>
      </div>
      <div className="exp-list-col-actions" onClick={(e) => e.stopPropagation()}>
        {expense.status === 'submitted' && (
          <>
            <button
              className="exp-row-action exp-row-action-ok"
              onClick={onApprove}
              disabled={isOwn}
              title={isOwn ? ownDisabledTitle : 'Godkänn'}
            >
              <CheckCircle size={14} />
            </button>
            <button
              className="exp-row-action exp-row-action-bad"
              onClick={onReject}
              disabled={isOwn}
              title={isOwn ? ownDisabledTitle : 'Avvisa'}
            >
              <XCircle size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const expQueueCss = `
    display: flex; align-items: center; gap: 12px; margin-bottom: 16px;
    padding: 10px 14px; background: white;
    border: 1px solid #e5e7eb; border-radius: 10px;
  }
    position: relative; flex: 1; min-width: 240px; max-width: 420px;
  }
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
    color: #9ca3af;
  }
    width: 100%; height: 36px; padding: 0 12px 0 34px;
    border: 1px solid #e5e7eb; border-radius: 8px;
    font-size: 0.875rem; background: #f9fafb;
    transition: all 0.15s;
  }
    outline: none; background: white;
    border-color: var(--bs-primary); box-shadow: 0 0 0 3px var(--rgba-primary-1);
  }
    display: flex; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;
  }
  .exp-pills {
    display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px;
  }
  .exp-pill {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 5px 12px; border: 1px solid #d1d5db; background: white;
    border-radius: 999px; font-size: 0.8125rem; font-weight: 500;
    color: #111827; cursor: pointer; transition: all 0.15s;
  }
  .exp-pill:hover { background: #f3f4f6; }
  .exp-pill.active {
    background: var(--rgba-primary-2); color: var(--bs-primary);
    border-color: var(--bs-primary); font-weight: 600;
  }
  .exp-pill-count {
    font-size: 0.6875rem; padding: 1px 6px; border-radius: 999px;
    background: #e5e7eb; color: #374151; font-weight: 600;
  }
  .exp-pill.active .exp-pill-count {
    background: var(--bs-primary); color: white;
  }

  .exp-selection-bar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 14px; background: var(--rgba-primary-1);
    border: 1px solid var(--bs-primary); border-radius: 8px;
    margin-bottom: 14px; color: #0f172a;
  }

  /* Kanban */
  .exp-kanban {
    display: flex; gap: 10px; overflow-x: auto; padding-bottom: 8px;
  }
  .exp-lane {
    flex: 1 1 220px; min-width: 220px; max-width: 340px;
    background: #f8fafc; border-radius: 10px;
    display: flex; flex-direction: column;
    max-height: calc(100vh - 280px);
  }
  .exp-lane-header {
    padding: 10px 12px; border-bottom: 1px solid #e5e7eb;
  }
  .exp-lane-title {
    font-size: 0.75rem; font-weight: 600; color: #374151;
    text-transform: uppercase; letter-spacing: 0.04em;
    display: flex; align-items: center; gap: 8px;
  }
  .exp-lane-count {
    font-size: 0.6875rem; padding: 1px 7px; border-radius: 999px;
    background: white; border: 1px solid #e5e7eb; color: #6b7280;
  }
  .exp-lane-date-filter {
    display: block;
    width: 100%;
    margin-top: 6px;
    padding: 3px 6px;
    font-size: 0.6875rem;
    color: #475569;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    cursor: pointer;
    outline: none;
  }
  .exp-lane-date-filter:focus { border-color: var(--primary); }
  .exp-lane-body {
    padding: 8px; overflow-y: auto; flex: 1;
    display: flex; flex-direction: column; gap: 6px;
  }
  .exp-lane-empty {
    text-align: center; color: #9ca3af; font-size: 0.75rem;
    padding: 20px 0;
  }

  /* Card */
  .exp-card {
    background: white; border: 1px solid #e5e7eb; border-radius: 8px;
    padding: 10px 12px; cursor: pointer;
    transition: all 0.15s;
  }
  .exp-card:hover {
    border-color: var(--bs-primary);
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    transform: translateY(-1px);
  }
  .exp-card-top {
    display: flex; justify-content: space-between; gap: 8px;
    align-items: flex-start; margin-bottom: 6px;
  }
  .exp-card-title {
    font-size: 0.8125rem; font-weight: 600; color: #0f172a;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    flex: 1; min-width: 0;
  }
  .exp-card-meta {
    display: flex; flex-wrap: wrap; gap: 10px;
    font-size: 0.6875rem; color: #64748b; margin-bottom: 8px;
  }
  .exp-card-meta-item { display: inline-flex; align-items: center; gap: 3px; }
  .exp-card-bottom {
    display: flex; justify-content: space-between; align-items: center;
  }
  .exp-card-cat {
    font-size: 0.6875rem; color: #64748b; text-transform: capitalize;
  }
  .exp-card-amt {
    font-size: 0.875rem; font-weight: 700; color: #0f172a;
    font-variant-numeric: tabular-nums;
  }

  /* Badges */
  .exp-badge {
    display: inline-block; padding: 2px 8px; border-radius: 999px;
    font-size: 0.625rem; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.02em;
    white-space: nowrap; flex-shrink: 0;
  }
  .exp-badge-warn { background: #fef3c7; color: #92400e; }
  .exp-badge-ok   { background: #d1fae5; color: #065f46; }
  .exp-badge-info { background: #dbeafe; color: #1e40af; }
  .exp-badge-done { background: #e0e7ff; color: #3730a3; }
  .exp-badge-bad  { background: #fee2e2; color: #991b1b; }
  .exp-badge-muted{ background: #f1f5f9; color: #475569; }

  /* List */
  .exp-list {
    background: white; border: 1px solid #e5e7eb; border-radius: 10px;
    overflow: hidden;
  }
  .exp-list-header, .exp-list-row {
    display: grid;
    grid-template-columns: 28px 90px 160px 1fr 140px 110px 130px 80px;
    gap: 12px; padding: 10px 14px; align-items: center;
    font-size: 0.8125rem;
  }
  .exp-list-header {
    background: #f8fafc; border-bottom: 1px solid #e5e7eb;
    font-size: 0.6875rem; font-weight: 600; color: #64748b;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .exp-list-row {
    border-bottom: 1px solid #f1f5f9; cursor: pointer;
    transition: background-color 0.15s;
  }
  .exp-list-row:hover { background: #f8fafc; }
  .exp-list-row:last-child { border-bottom: none; }
  .exp-list-col-desc {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    color: #0f172a; font-weight: 500;
  }
  .exp-list-col-amt {
    text-align: right; font-weight: 600; font-variant-numeric: tabular-nums; color: #0f172a;
  }
  .exp-list-col-actions { display: flex; gap: 4px; justify-content: flex-end; }
  .exp-cat-chip {
    display: inline-block; padding: 2px 8px; border-radius: 6px;
    background: #f3f4f6; color: #374151; font-size: 0.6875rem;
    text-transform: capitalize;
  }
  .exp-row-action {
    width: 28px; height: 28px; border-radius: 6px;
    background: white; border: 1px solid #e5e7eb; color: #6b7280;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
  }
  .exp-row-action-ok:hover { background: #d1fae5; border-color: #10b981; color: #065f46; }
  .exp-row-action-bad:hover { background: #fee2e2; border-color: #ef4444; color: #991b1b; }

  .exp-empty {
    text-align: center; padding: 48px 16px; color: #9ca3af;
    background: white; border: 1px dashed #e5e7eb; border-radius: 10px;
  }
`;

export default ExpenseApprovalQueuePage;
